import React, { FC, Fragment } from 'react';
import { Grid } from '@material-ui/core';

import { Group } from 'api';
import { PostAction } from 'types/app';
import { Desktop, EmptyState, Layouts, Mobile } from 'components';
import { useCurrentUser, useFollowed, useLocalStorage } from 'hooks';
import { Feed } from 'features/feed';
import { IPostRenderProps, Post } from 'features/posting';
import { canModerateGroupContent } from 'features/groups/helpers';
import { DEFAULT_SORT, GROUP_FEED_KEY } from 'constants/index';

import { DesktopBanner, FeedMenu, MobileBanner } from '../Group';
import CreatePostPlaceholder from './CreatePostPlaceholder';
import Members from './Members';
import Report from './Report/ReportIndex';

interface IProps {
  isFollowing: boolean;
  group: Group;
}

const GroupPosts: FC<IProps> = props => {
  const { group, isFollowing } = props;
  const currentUser = useCurrentUser();
  const { postable } = useFollowed();
  const [sort, setSort] = useLocalStorage('timelineSort', DEFAULT_SORT);

  const { list } = postable;
  const selectedGroup = getSelectedGroup();
  const groupIds = getGroupIds();

  function getGroupIds() {
    return selectedGroup ? [selectedGroup.id] : [];
  }

  function getSelectedGroup() {
    return list.find(postableGroup => {
      return group.id === postableGroup.id;
    });
  }

  function handleSort() {
    setSort(!sort);
  }

  function renderMenu() {
    return <FeedMenu enableSort selectedGroup={group} onSort={handleSort} sort={sort} />;
  }

  function renderCreatePost() {
    if (!selectedGroup) {
      return null;
    }

    return (
      <Post action={PostAction.Create} selectedGroup={selectedGroup} groupIds={groupIds}>
        {({ toggleState }: IPostRenderProps) => <CreatePostPlaceholder toggleState={toggleState} />}
      </Post>
    );
  }

  function renderFeed() {
    const emptyStateName = selectedGroup ? 'groupMember' : 'groupJoin';
    const variables = {
      sort,
      key: GROUP_FEED_KEY,
      id: group.id,
    };

    return (
      <Feed
        emptyState={() => <EmptyState name={emptyStateName} />}
        variables={variables}
        selectedGroup={group}
        header={renderCreatePost()}
      />
    );
  }

  function renderSidebar() {
    const canRunReport = canModerateGroupContent(currentUser, group.id);

    return (
      <Grid container direction="column" wrap="nowrap" spacing={3}>
        {canRunReport && <Report group={group} />}
        <Grid item>
          <Members group={group} />
        </Grid>
      </Grid>
    );
  }

  function renderBanner() {
    const bannerProps = {
      group,
      menu: renderMenu(),
    };

    return (
      <Fragment>
        <Desktop>
          <DesktopBanner {...bannerProps} />
        </Desktop>
        <Mobile>
          <MobileBanner isFollowing={isFollowing} {...bannerProps} />
        </Mobile>
      </Fragment>
    );
  }

  return (
    <Layouts.AppMain2Column
      hideRightOnMobile
      desktopSplit="8/4"
      header={renderBanner()}
      left={renderFeed()}
      right={renderSidebar()}
    />
  );
};

export default GroupPosts;
